import { Cake, Star, Wallet, GraduationCap, CalendarClock } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Link } from 'react-router'
import { ChartCard, DateValue } from '@/components/common'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState } from '@/components/common/EmptyState'
import { useUpcomingEvents } from '../api'
import type { UpcomingEvent } from '../types'
import { cn } from '@/lib/utils'

const EVENT_STYLES: Record<string, { icon: LucideIcon; tint: string; row: string }> = {
  BIRTHDAY: { icon: Cake, tint: 'text-[var(--color-primary)]', row: 'bg-[var(--color-primary-soft)]/45' },
  WORK_ANNIVERSARY: { icon: Star, tint: 'text-[var(--color-warning)]', row: 'bg-[var(--color-tint-orange)]/55' },
  PAYROLL: { icon: Wallet, tint: 'text-[var(--color-success)]', row: 'bg-[var(--color-tint-green)]/55' },
  TRAINING: { icon: GraduationCap, tint: 'text-[var(--color-info)]', row: 'bg-[var(--color-tint-blue)]/55' },
}

function initials(name: string) {
  return name.split(' ').filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('')
}

/** Birthdays, anniversaries and scheduled events for the next 30 days. */
export function UpcomingEventsCard() {
  // Four is what the card shows without scrolling; the request asks for a few more so
  // the scroll is real rather than a promise.
  const { data, isLoading } = useUpcomingEvents(12)

  return (
    <ChartCard
      title="Upcoming Events"
      icon={CalendarClock}
      filter={
        <Link to="/employees" className="text-xs font-medium text-[var(--color-primary)] hover:underline">
          View All
        </Link>
      }
      bodyClassName="pt-1"
    >
      {isLoading && (
        <div className="space-y-2 py-1">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-[58px] rounded-[var(--radius-control)]" />
          ))}
        </div>
      )}

      {data && data.length === 0 && (
        <EmptyState title="Nothing coming up" description="Birthdays and anniversaries appear here." />
      )}

      {/*
        Four at a time, scrolling inside the card. The list used to grow with whatever the
        month held, which stretched the whole row and pushed the two charts beside it out of
        shape.
      */}
      {data && data.length > 0 && (
        <ul className="max-h-[268px] space-y-2 overflow-y-auto overscroll-contain py-1">
          {data.map((event: UpcomingEvent, index) => {
            const style = EVENT_STYLES[event.type] ?? EVENT_STYLES.BIRTHDAY!
            const Icon = style.icon
            return (
              <li
                key={`${event.type}-${event.subjectId}-${index}`}
                className={cn(
                  'flex items-center gap-3 rounded-[var(--radius-control)] px-3 py-2.5',
                  style.row,
                )}
              >
                <span className="grid size-9 shrink-0 place-items-center rounded-full bg-[var(--color-card)] text-xs font-semibold text-[var(--color-primary)]">
                  {initials(event.subtitle)}
                </span>

                {/* min-w-0 lets the flex child shrink so truncate actually engages. */}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium leading-tight text-[var(--color-heading)]">
                    {event.title}
                  </span>
                  <span className="block truncate text-xs leading-tight text-[var(--color-muted)]">
                    {event.subtitle}
                  </span>
                  {/* The date sits under the name rather than beside it: at 3 columns wide
                      there is not enough room for both without truncating the name away. */}
                  <DateValue
                    value={event.eventDate}
                    className="mt-0.5 block text-[11px] leading-tight text-[var(--color-muted)]"
                  />
                </span>

                <span className={cn('grid size-8 shrink-0 place-items-center rounded-lg bg-[var(--color-card)]', style.tint)}>
                  <Icon className="size-4" aria-hidden="true" />
                </span>
              </li>
            )
          })}
        </ul>
      )}
    </ChartCard>
  )
}
